import {
  BriefcaseIcon,
  ExclamationTriangleIcon,
  TruckIcon,
} from "@heroicons/react/24/outline";
import { DateTime } from "luxon";
import { useDrag } from "react-dnd";
import { useTranslation } from "react-i18next";

import { CheckFeatureFlag } from "../../hooks/useFeatureFlag";
import { DragTypes } from "../../models/DragTypes";
import { DropOffSlot, IDropOffDragItem, IDropOffLeadRow } from "../../models/DropOffPlanner";
import classNames from "../../utils/classNames";
import openInNewTab from "../../utils/openInNewTab";
import Tooltip from "../shared/Tooltip";

interface DropOffLeadCardProps {
  lead: IDropOffLeadRow;
}

const ageDays = (iso: string): number => {
  const created = DateTime.fromISO(iso);
  if (!created.isValid) return 0;
  return Math.max(0, Math.floor(DateTime.local().diff(created, "days").days));
};

const ageBorderClasses = (days: number): string => {
  if (days >= 14) return "border-l-4 border-l-red-500";
  if (days >= 7) return "border-l-4 border-l-amber-500";
  return "border-l-4 border-l-green-500";
};

export default function DropOffLeadCard({ lead }: DropOffLeadCardProps) {
  const { t } = useTranslation("leads");
  const dragItem: IDropOffDragItem = {
    leadId: lead.id,
    currentScheduledOn: lead.scheduledCollectionOn,
    currentSlot: lead.scheduledCollectionOn ? lead.scheduledCollectionSlot : null,
  };

  const [{ isDragging }, drag] = useDrag(
    () => ({
      type: DragTypes.DROP_OFF_LEAD,
      item: dragItem,
      collect: (monitor) => ({
        isDragging: monitor.isDragging(),
      }),
    }),
    [lead.id, lead.scheduledCollectionOn, lead.scheduledCollectionSlot]
  );

  const days = ageDays(lead.created);

  const leadManagementEnabled = CheckFeatureFlag("Lead Management");

  const handleOpen = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (leadManagementEnabled) {
      openInNewTab(`/leads/${lead.id}`);
    } else {
      openInNewTab(`/vehicles/${lead.vehicleId}`);
    }
  };

  const slotLabel =
    lead.scheduledCollectionOn && lead.scheduledCollectionSlot === DropOffSlot.AM
      ? t("AM")
      : lead.scheduledCollectionOn && lead.scheduledCollectionSlot === DropOffSlot.PM
        ? t("PM")
        : null;

  const iconButtonClasses =
    "inline-flex h-6 w-6 items-center justify-center rounded border border-gray-200 bg-white text-gray-500 hover:bg-gray-50 hover:text-gray-700 disabled:opacity-50";

  return (
    <div
      ref={drag}
      className={classNames(
        "bg-white rounded-md border border-gray-200 shadow-sm px-2.5 py-2 cursor-grab select-none",
        ageBorderClasses(days),
        isDragging ? "opacity-40" : "hover:shadow"
      )}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-x-1.5">
            <span className="text-sm font-semibold text-gray-900 truncate">
              {lead.vrm || t("No VRM")}
            </span>
            {lead.hasAlert && (
              <Tooltip content={t("Has alert")}>
                <ExclamationTriangleIcon className="h-3.5 w-3.5 text-red-600" />
              </Tooltip>
            )}
            {lead.isVan && (
              <Tooltip content={t("Van")}>
                <TruckIcon className="h-3.5 w-3.5 text-gray-500" />
              </Tooltip>
            )}
          </div>
          <div className="text-xs text-gray-700 truncate">
            {[lead.combinedMake, lead.combinedModel].filter(Boolean).join(" ") || "—"}
          </div>
          {(lead.source || lead.postcode) && (
            <div className="text-[11px] text-gray-500 truncate">
              {lead.source && <span>{t("Source: {{source}}", { source: lead.source })}</span>}
              {lead.source && lead.postcode && <span> · </span>}
              {lead.postcode && <span>{lead.postcode}</span>}
            </div>
          )}
        </div>
        <div className="flex flex-col items-end gap-y-1 shrink-0">
          {slotLabel && (
            <span className="text-[10px] font-semibold uppercase tracking-wide rounded px-1.5 py-0.5 bg-primary-100 text-primary-700">
              {slotLabel}
            </span>
          )}
          <div className="flex items-center gap-x-1">
            <button
              type="button"
              onClick={handleOpen}
              className={iconButtonClasses}
              title={leadManagementEnabled ? t("Open lead") : t("Open vehicle")}
            >
              {leadManagementEnabled ? (
                <BriefcaseIcon className="h-3.5 w-3.5" />
              ) : (
                <TruckIcon className="h-3.5 w-3.5" />
              )}
            </button>
          </div>
        </div>
      </div>

      <div className="mt-0.5 text-[11px] text-gray-500">
        {t("Created {{date}}", { date: DateTime.fromISO(lead.created).toFormat("dd/MM/yyyy") })}
      </div>
    </div>
  );
}
